import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { hasRole } from "@/lib/roles";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Circle, Loader2, FileText, BadgeCheck, AlertTriangle, RotateCcw, PauseCircle, XCircle } from "lucide-react";

/**
 * WorkflowStepper – Visualizes the ESG rating process workflow
 * (Disciplinare § 6.5 Fasi 1-10) and allows authorized users to advance phases
 * and record formal verbals (Stage 1, Stage 2, Technical Review, Decision).
 */
export default function WorkflowStepper({ assessmentId }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [openDialog, setOpenDialog] = useState(null); // 'stage1'|'stage2'|'review'|'decision'|null

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/assessments/${assessmentId}/workflow`);
      setState(data);
    } catch (e) {
      toast.error("Errore caricamento workflow");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [assessmentId]);

  const [deliberanteId, setDeliberanteId] = useState(null);
  useEffect(() => {
    api.get(`/assessments/${assessmentId}/team`)
      .then(({ data }) => setDeliberanteId(data.team?.deliberante?.coi?.confirmed ? data.team.deliberante.user_id : null))
      .catch(() => {});
  }, [assessmentId]);

  async function transition(target, note = "") {
    setTransitioning(true);
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/workflow/transition`, {
        target_status: target, note,
      });
      setState(data);
      toast.success(`Stato aggiornato: ${data.status_label}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transizione fallita");
    } finally { setTransitioning(false); }
  }

  async function resetToDraft() {
    if (!confirm("Sei sicuro di voler riportare l'assessment a stato BOZZA? Verbali e delibera resteranno salvati ma il certificato perderà validità fino alla nuova emissione.")) return;
    setTransitioning(true);
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/workflow/transition`, {
        target_status: "draft",
        note: "Reset admin",
      });
      setState(data);
      toast.success("Assessment riportato a BOZZA");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fallito");
    } finally { setTransitioning(false); }
  }

  const isAssignedDeliberante = deliberanteId && deliberanteId === user?.id;

  function askReason(target) {
    const label = target === "sospeso" ? "sospensione" : "revoca (definitiva, § 6.7 Disciplinare)";
    const reason = window.prompt(`Motivazione obbligatoria per la ${label} (almeno 10 caratteri):`);
    if (reason === null) return;
    if (reason.trim().length < 10) { toast.error("Motivazione obbligatoria (almeno 10 caratteri)"); return; }
    transition(target, reason.trim());
  }

  if (loading) {
    return (
      <Card className="p-6 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento workflow...
      </Card>
    );
  }
  if (!state) return null;

  const currentIdx = state.phases.findIndex(p => p.code === state.status);
  const can = (...r) => hasRole(user, ...r);
  const canTransitionFrom = {
    draft: ["admin", "valuatore", "cliente"],
    domanda_inviata: ["admin", "valuatore"],
    contratto_firmato: ["admin", "valuatore"],
    riesame_domanda: ["admin", "valuatore"],
    piano_audit: ["valuatore"],
    analisi_documentale: ["valuatore"],
    stage1_completato: ["valuatore"],
    stage2_completato: ["valuatore"],
    rating_elaborato: ["tech_reviewer"],
    riesame_tecnico: ["decision_maker"],
    delibera: ["decision_maker"],
  };
  const allowedNext = {
    draft: { code: "domanda_inviata", label: "Invia domanda" },
    domanda_inviata: { code: "contratto_firmato", label: "Firma contratto" },
    contratto_firmato: { code: "riesame_domanda", label: "Avvia riesame domanda" },
    riesame_domanda: { code: "piano_audit", label: "Predisponi piano audit" },
    piano_audit: { code: "analisi_documentale", label: "Avvia trasmissione documentale" },
    analisi_documentale: { code: "stage1_completato", label: "Chiudi Stage 1" },
    stage1_completato: { code: "stage2_completato", label: "Chiudi Stage 2" },
    stage2_completato: { code: "rating_elaborato", label: "Elabora rating" },
    rating_elaborato: { code: "riesame_tecnico", label: "Avvia riesame tecnico" },
    riesame_tecnico: { code: "delibera", label: "Passa a delibera" },
    delibera: { code: "emesso", label: "Emetti certificato" },
  };
  const nextHint = allowedNext[state.status];
  const allowedRoles = canTransitionFrom[state.status] || ["admin"];
  const canAdvance = can(...allowedRoles);

  return (
    <Card className="bg-white rounded-3xl shadow-sm border border-slate-200" data-testid="workflow-stepper">
      <div className="px-6 py-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">
            Workflow di certificazione
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Processo conforme al Disciplinare DT-RTG-01 § 6.5
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-1 rounded-full bg-viva-50 text-viva-700 text-xs font-semibold uppercase tracking-wide">
            {state.status_label}
          </div>
          <div className="text-xs text-slate-500">{state.progress}% completato</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-6 pt-4">
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-viva-500 to-emerald-500 transition-all"
            style={{ width: `${state.progress}%` }}
          />
        </div>
      </div>

      {/* Phases */}
      <ol className="px-6 py-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {state.phases.map((p, idx) => {
          const done = idx < currentIdx || (state.status === "emesso" && idx <= currentIdx);
          const active = idx === currentIdx;
          return (
            <li
              key={p.code}
              data-testid={`wf-phase-${p.code}`}
              className={`p-3 rounded-xl border text-sm flex items-start gap-2 transition ${
                done ? "bg-emerald-50 border-emerald-200" :
                active ? "bg-viva-50 border-viva-300 ring-2 ring-viva-200" :
                "bg-slate-50 border-slate-200"
              }`}
            >
              {done ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                    : <Circle className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-viva-600" : "text-slate-400"}`} />}
              <div>
                <div className={`text-[10px] font-mono ${active ? "text-viva-700" : "text-slate-400"}`}>
                  {p.number ? `Fase ${p.number}` : "Pre-fase"}
                </div>
                <div className={`font-medium text-xs ${active ? "text-viva-800" : done ? "text-emerald-800" : "text-slate-600"}`}>
                  {p.label}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Action panel */}
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 rounded-b-3xl">
        <div className="text-xs text-slate-500">
          {state.certificate_code ? (
            <span>
              Certificato <code className="font-mono text-viva-700">{state.certificate_code}</code>
              {state.expires_at && (
                <span className="ml-2 text-slate-400">
                  · scade il {new Date(state.expires_at).toLocaleDateString("it-IT")}
                </span>
              )}
            </span>
          ) : "Nessun certificato ancora emesso"}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Admin-only: reset to draft (useful for testing or correcting errors) */}
          {can("admin") && !["draft", "emesso", "sospeso", "revocato", "scaduto", "archived"].includes(state.status) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={resetToDraft}
              disabled={transitioning}
              data-testid="wf-reset-btn"
              className="text-slate-500 hover:text-slate-700"
              title="Riporta l'assessment a stato BOZZA (admin)"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Riporta a Bozza
            </Button>
          )}
          {/* From emesso → Sospendi / Revoca */}
          {isAssignedDeliberante && state.status === "emesso" && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => askReason("sospeso")}
                disabled={transitioning}
                data-testid="wf-suspend-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <PauseCircle className="w-3.5 h-3.5 mr-1" /> Sospendi
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => askReason("revocato")}
                disabled={transitioning}
                data-testid="wf-revoke-btn"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" /> Revoca
              </Button>
            </>
          )}
          {/* From sospeso → Riattiva / Revoca */}
          {isAssignedDeliberante && state.status === "sospeso" && (
            <>
              <Button
                size="sm"
                onClick={() => transition("emesso")}
                disabled={transitioning}
                data-testid="wf-reactivate-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Riattiva
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => askReason("revocato")}
                disabled={transitioning}
                data-testid="wf-revoke-btn"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" /> Revoca
              </Button>
            </>
          )}
          {/* Phase-specific verbal entry buttons */}
          {can("valuatore") && state.status === "piano_audit" && (
            <Button size="sm" variant="outline" onClick={() => setOpenDialog("stage1")}
                    data-testid="wf-open-stage1">
              <FileText className="w-3.5 h-3.5 mr-1" /> Verbale Stage 1
            </Button>
          )}
          {can("valuatore") && state.status === "stage1_completato" && (
            <Button size="sm" variant="outline" onClick={() => setOpenDialog("stage2")}
                    data-testid="wf-open-stage2">
              <FileText className="w-3.5 h-3.5 mr-1" /> Verbale Stage 2
            </Button>
          )}
          {can("tech_reviewer") && ["stage2_completato", "rating_elaborato"].includes(state.status) && (
            <Button size="sm" variant="outline" onClick={() => setOpenDialog("review")}
                    data-testid="wf-open-review">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Riesame tecnico
            </Button>
          )}
          {can("decision_maker") && state.status === "riesame_tecnico" && (
            <Button size="sm" variant="outline" onClick={() => setOpenDialog("decision")}
                    data-testid="wf-open-decision">
              <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Delibera
            </Button>
          )}
          {/* Forward transition button */}
          {nextHint && canAdvance && (
            <Button
              size="sm"
              onClick={() => transition(nextHint.code)}
              disabled={transitioning}
              data-testid="wf-advance-btn"
              className="bg-viva-600 hover:bg-viva-700 text-white"
            >
              {transitioning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              {nextHint.label}
            </Button>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <StageDialog open={openDialog === "stage1"} title="Verbale Audit Stage 1"
                   endpoint={`/assessments/${assessmentId}/workflow/verbal-stage1`}
                   onClose={() => setOpenDialog(null)} onDone={load} />
      <StageDialog open={openDialog === "stage2"} title="Verbale Audit Stage 2"
                   endpoint={`/assessments/${assessmentId}/workflow/verbal-stage2`}
                   onClose={() => setOpenDialog(null)} onDone={load} />
      <ReviewDialog open={openDialog === "review"} assessmentId={assessmentId}
                    onClose={() => setOpenDialog(null)} onDone={load} />
      <DecisionDialog open={openDialog === "decision"} assessmentId={assessmentId}
                      onClose={() => setOpenDialog(null)} onDone={load} />
    </Card>
  );
}

// ---------------- Sub-dialogs ----------------
function StageDialog({ open, title, endpoint, onClose, onDone }) {
  const [form, setForm] = useState({
    audit_date: new Date().toISOString().slice(0, 10),
    location: "", participants: "", findings: "",
    non_conformities: "", recommendations: "", outcome: "positivo",
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.post(endpoint, form);
      toast.success("Verbale registrato");
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="wf-stage-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label>Data audit</Label>
            <Input type="date" value={form.audit_date}
                   onChange={(e) => setForm({ ...form, audit_date: e.target.value })} />
          </div>
          <div>
            <Label>Luogo</Label>
            <Input value={form.location}
                   onChange={(e) => setForm({ ...form, location: e.target.value })}
                   placeholder="Sede operativa, on-site / remoto..." />
          </div>
          <div className="col-span-2">
            <Label>Partecipanti</Label>
            <Input value={form.participants}
                   onChange={(e) => setForm({ ...form, participants: e.target.value })}
                   placeholder="Nome 1, Nome 2, ..." />
          </div>
          <div className="col-span-2">
            <Label>Rilievi / findings</Label>
            <Textarea rows={3} value={form.findings}
                      onChange={(e) => setForm({ ...form, findings: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Non conformità</Label>
            <Textarea rows={2} value={form.non_conformities}
                      onChange={(e) => setForm({ ...form, non_conformities: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Raccomandazioni</Label>
            <Textarea rows={2} value={form.recommendations}
                      onChange={(e) => setForm({ ...form, recommendations: e.target.value })} />
          </div>
          <div>
            <Label>Esito</Label>
            <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="positivo">Positivo</SelectItem>
                <SelectItem value="con_riserve">Con riserve</SelectItem>
                <SelectItem value="negativo">Negativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="wf-stage-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva verbale"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ open, assessmentId, onClose, onDone }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    reviewer_name: user?.name || "",
    review_date: new Date().toISOString().slice(0, 10),
    methodology_check: true, completeness_check: true,
    outcome: "approvato", notes: "",
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.post(`/assessments/${assessmentId}/workflow/technical-review`, form);
      toast.success("Riesame tecnico registrato");
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="wf-review-dialog">
        <DialogHeader>
          <DialogTitle>Riesame tecnico indipendente (Fase 8)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome riesaminatore</Label>
              <Input value={form.reviewer_name}
                     onChange={(e) => setForm({ ...form, reviewer_name: e.target.value })} />
            </div>
            <div>
              <Label>Data riesame</Label>
              <Input type="date" value={form.review_date}
                     onChange={(e) => setForm({ ...form, review_date: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.methodology_check}
                     onChange={(e) => setForm({ ...form, methodology_check: e.target.checked })} />
              Metodologia applicata correttamente
            </label>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.completeness_check}
                     onChange={(e) => setForm({ ...form, completeness_check: e.target.checked })} />
              Completezza verificata
            </label>
          </div>
          <div>
            <Label>Esito</Label>
            <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="approvato">Approvato</SelectItem>
                <SelectItem value="richiede_integrazioni">Richiede integrazioni</SelectItem>
                <SelectItem value="respinto">Respinto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note</Label>
            <Textarea rows={3} value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="wf-review-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registra riesame"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ open, assessmentId, onClose, onDone }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    decision_maker_name: user?.name || "",
    decision_date: new Date().toISOString().slice(0, 10),
    outcome: "positivo", final_rating: "", band: "V4",
    motivazione: "", constraints: "",
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.post(`/assessments/${assessmentId}/workflow/decision`, {
        ...form,
        final_rating: form.final_rating ? parseFloat(form.final_rating) : null,
      });
      toast.success("Delibera registrata");
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="wf-decision-dialog">
        <DialogHeader>
          <DialogTitle>Delibera di attestazione (Fase 9)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome Decision Maker</Label>
              <Input value={form.decision_maker_name}
                     onChange={(e) => setForm({ ...form, decision_maker_name: e.target.value })} />
            </div>
            <div>
              <Label>Data delibera</Label>
              <Input type="date" value={form.decision_date}
                     onChange={(e) => setForm({ ...form, decision_date: e.target.value })} />
            </div>
            <div>
              <Label>Esito</Label>
              <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="positivo">Positivo (rilascio)</SelectItem>
                  <SelectItem value="negativo">Negativo (non rilascio)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Banda VIVA</Label>
              <Select value={form.band} onValueChange={(v) => setForm({ ...form, band: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["V1","V2","V3","V4","V5","V6","V7"].map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Rating finale (0-100)</Label>
              <Input type="number" step="0.1" value={form.final_rating}
                     onChange={(e) => setForm({ ...form, final_rating: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Motivazione</Label>
            <Textarea rows={3} value={form.motivazione}
                      onChange={(e) => setForm({ ...form, motivazione: e.target.value })} />
          </div>
          <div>
            <Label>Condizioni / piano di miglioramento (opzionale)</Label>
            <Textarea rows={2} value={form.constraints}
                      onChange={(e) => setForm({ ...form, constraints: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="wf-decision-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delibera"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
